// Pure maths for the pricing from 1 November 2026: storage-pack billing, the country examples on
// /pricing-changes-2026 (per order and per month, today vs new) and picking a default country.
// No I/O; every figure the pages show is reproducible and unit-tested (changes2026.test.ts).

import {
  NEW_PLANS,
  STORAGE_PACKS,
  TODAY_PLANS,
  type NewPlan,
  type StoragePack,
  type TodayPlan,
} from "@/content/pricing/plans";
import {
  COUNTRY_EXAMPLES,
  DEFAULT_COUNTRY,
  SALES_LEVELS_GBP,
  USD_PER_GBP,
  type CountryExample,
  type CountryId,
} from "@/content/pricing/countries";
import { orderBreakdown } from "./orderFee";

// ---------------------------------------------------------------------------------------------
// Storage packs

export interface PackMix {
  /** Packs used, largest first, with a count for each. Empty when nothing extra is stored. */
  packs: { pack: StoragePack; count: number }[];
  /** US$ a month. */
  priceUsd: number;
  /** GB the mix covers. */
  gb: number;
}

/**
 * The cheapest mix of packs that covers `extraGb` above the plan's allowance: what's billed
 * automatically. Exhaustive over the two larger packs (small search space), smallest pack fills
 * the rest. Ties go to fewer GB, then fewer packs.
 */
export function cheapestPackMix(extraGb: number, packs: StoragePack[] = STORAGE_PACKS): PackMix {
  const need = Math.max(0, Math.ceil(extraGb));
  if (need === 0) return { packs: [], priceUsd: 0, gb: 0 };
  const sorted = [...packs].sort((a, b) => b.gb - a.gb);
  let best: { counts: number[]; price: number; gb: number; n: number } | null = null;
  const counts = new Array(sorted.length).fill(0);
  const search = (i: number, covered: number, price: number) => {
    if (i === sorted.length - 1) {
      const p = sorted[i];
      const c = Math.max(0, Math.ceil((need - covered) / p.gb));
      counts[i] = c;
      const total = { price: price + c * p.priceUsd, gb: covered + c * p.gb, n: counts.reduce((s, x) => s + x, 0) };
      if (
        !best ||
        total.price < best.price ||
        (total.price === best.price && (total.gb < best.gb || (total.gb === best.gb && total.n < best.n)))
      ) {
        best = { counts: [...counts], ...total };
      }
      return;
    }
    const p = sorted[i];
    const max = Math.ceil(Math.max(0, need - covered) / p.gb);
    for (let c = 0; c <= max; c++) {
      counts[i] = c;
      search(i + 1, covered + c * p.gb, price + c * p.priceUsd);
    }
    counts[i] = 0;
  };
  search(0, 0, 0);
  const b = best as unknown as { counts: number[]; price: number; gb: number };
  return {
    packs: sorted.map((pack, i) => ({ pack, count: b.counts[i] })).filter((x) => x.count > 0),
    priceUsd: b.price,
    gb: b.gb,
  };
}

/** The monthly cost of storing `storedGb` on a plan: its allowance, then the cheapest pack mix. */
export const extraStorageFor = (storedGb: number, plan: NewPlan) => cheapestPackMix(storedGb - plan.storageGb);

// ---------------------------------------------------------------------------------------------
// Country examples

export const countryExample = (id: CountryId): CountryExample => {
  const c = COUNTRY_EXAMPLES.find((x) => x.id === id);
  if (!c) throw new Error(`unknown country example ${id}`);
  return c;
};

/** The example for a browser region (ISO alpha-2), or the default (UK). */
export function exampleForRegion(region: string | null | undefined): CountryId {
  const r = (region || "").toUpperCase();
  return COUNTRY_EXAMPLES.find((c) => c.regions.includes(r))?.id ?? DEFAULT_COUNTRY;
}

/** The region from the browser's languages ("es-MX" → "MX"), as the fees-by-country table does. */
export function browserRegion(langs: readonly string[]): string | null {
  for (const lang of langs) {
    if (!lang) continue;
    try {
      const region = new Intl.Locale(lang).region;
      if (region && /^[A-Z]{2}$/.test(region)) return region;
    } catch {
      const m = /[-_]([A-Z]{2})$/i.exec(lang);
      if (m) return m[1].toUpperCase();
    }
  }
  return null;
}

type PricedPlan = { name: string; priceUsd: number; commissionRate: number };

/**
 * All-in share of monthly sales: (plan fee + commission + card fees) / sales, on whichever plan
 * costs least at that level. `salesLocal` in the country's currency; orders = sales / average order.
 * Ignores free-download and storage charges (the pricing model does too for these examples).
 */
export function allInShare(country: CountryExample, salesLocal: number, plans: PricedPlan[]) {
  const orders = salesLocal / country.avgOrder;
  const card = salesLocal * country.cardFee.percent + orders * country.cardFee.fixed;
  let best: { share: number; plan: PricedPlan } | null = null;
  for (const plan of plans) {
    const feeLocal = (plan.priceUsd / USD_PER_GBP) * country.fxPerGbp;
    const share = (feeLocal + salesLocal * plan.commissionRate + card) / salesLocal;
    if (!best || share < best.share) best = { share, plan };
  }
  return best as { share: number; plan: PricedPlan };
}

/** Round a sales level to two significant figures: 2,707 → 2,700; 41,565 → 42,000. */
export function roundSales(n: number): number {
  if (n <= 0) return 0;
  const mag = 10 ** (Math.floor(Math.log10(n)) - 1);
  return Math.round(n / mag) * mag;
}

export interface MonthlyExample {
  salesGbp: number;
  /** Sales in local currency, rounded to two significant figures for display. */
  salesLocal: number;
  today: { share: number; plan: string };
  next: { share: number; plan: string };
  /** "pricing-model" figures are the given model results; "computed" come from allInShare(). */
  source: "pricing-model" | "computed";
  /** How much more the photographer keeps a month, local currency (rounded to 2 s.f.). */
  keepMoreLocal: number;
}

/** The three monthly levels for a country, today → from 1 November. */
export function monthlyExamples(country: CountryExample): MonthlyExample[] {
  return SALES_LEVELS_GBP.map((salesGbp, i) => {
    const exact = salesGbp * country.fxPerGbp;
    const today = allInShare(country, exact, TODAY_PLANS);
    const next = allInShare(country, exact, NEW_PLANS);
    const given = country.given?.[i];
    const shareToday = given ? given.today : Math.round(today.share * 1000) / 1000;
    const shareNext = given ? given.next : Math.round(next.share * 1000) / 1000;
    return {
      salesGbp,
      salesLocal: roundSales(exact),
      today: { share: shareToday, plan: today.plan.name },
      next: { share: shareNext, plan: next.plan.name },
      source: given ? "pricing-model" : "computed",
      keepMoreLocal: roundSales((shareToday - shareNext) * exact),
    };
  });
}

export interface OrderExample {
  from: TodayPlan;
  to: NewPlan;
  /** All in the country's minor units. */
  order: number;
  cardFee: number;
  commissionToday: number;
  commissionNext: number;
  keepToday: number;
  keepNext: number;
}

/** A typical order in the country, on each of today's plans and the plan it moves to. */
export function orderExamples(country: CountryExample, amount: number = country.avgOrder): OrderExample[] {
  return TODAY_PLANS.map((from) => {
    const to = NEW_PLANS.find((p) => p.id === from.movesTo) as NewPlan;
    const a = orderBreakdown(amount, from.commissionRate, country.cardFee, country.currency);
    const b = orderBreakdown(amount, to.commissionRate, country.cardFee, country.currency);
    return {
      from,
      to,
      order: a.order,
      cardFee: a.cardFee,
      commissionToday: a.commission,
      commissionNext: b.commission,
      keepToday: a.keep,
      keepNext: b.keep,
    };
  });
}
